import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  ClockIcon,
  ServerIcon,
  UserIcon,
  EnvelopeIcon,
  ShieldCheckIcon,
  BugAntIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/hooks/useAuth';
import toast from 'react-hot-toast';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  category: 'api' | 'auth' | 'email' | 'database' | 'user_action' | 'system' | 'security';
  message: string;
  createdBy: string; // Who performed the action or triggered the event
  details?: {
    method?: string;
    endpoint?: string;
    statusCode?: number;
    userId?: string;
    userEmail?: string;
    ip?: string;
    duration?: string;
    requestId?: string;
  };
  metadata?: Record<string, any>;
}

const SystemLogsPage: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);

  useEffect(() => {
    fetchLogs();
  }, [searchQuery, levelFilter, categoryFilter]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh) {
      interval = setInterval(fetchLogs, 5000); // Refresh every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, searchQuery, levelFilter, categoryFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      // No logs available until API is implemented
      let filteredLogs: LogEntry[] = [];

      if (searchQuery.trim()) {
        filteredLogs = filteredLogs.filter(log => 
          log.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
          log.details?.userEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          log.details?.endpoint?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      if (levelFilter !== 'all') {
        filteredLogs = filteredLogs.filter(log => log.level === levelFilter);
      }

      if (categoryFilter !== 'all') {
        filteredLogs = filteredLogs.filter(log => log.category === categoryFilter);
      }

      setLogs(filteredLogs);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      toast.error('Failed to fetch system logs');
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (level: LogEntry['level']) => {
    const iconClass = "h-4 w-4";
    switch (level) {
      case 'info':
        return <InformationCircleIcon className={`${iconClass} text-blue-600`} />;
      case 'warn':
        return <ExclamationTriangleIcon className={`${iconClass} text-yellow-600`} />;
      case 'error':
        return <XCircleIcon className={`${iconClass} text-red-600`} />;
      case 'debug':
        return <BugAntIcon className={`${iconClass} text-purple-600`} />;
      default:
        return <InformationCircleIcon className={`${iconClass} text-gray-600`} />;
    }
  };

  const getCategoryIcon = (category: LogEntry['category']) => {
    const iconClass = "h-4 w-4";
    switch (category) {
      case 'api':
        return <ServerIcon className={`${iconClass} text-blue-600`} />;
      case 'auth':
        return <ShieldCheckIcon className={`${iconClass} text-green-600`} />;
      case 'email':
        return <EnvelopeIcon className={`${iconClass} text-purple-600`} />;
      case 'database':
        return <CircleStackIcon className={`${iconClass} text-orange-600`} />;
      case 'user_action':
        return <UserIcon className={`${iconClass} text-indigo-600`} />;
      case 'system':
        return <ServerIcon className={`${iconClass} text-gray-600`} />;
      case 'security':
        return <ShieldCheckIcon className={`${iconClass} text-red-600`} />;
      default:
        return <ClipboardDocumentListIcon className={`${iconClass} text-gray-600`} />;
    }
  };

  const getLevelBadgeClass = (level: LogEntry['level']) => {
    switch (level) {
      case 'info':
        return 'bg-blue-100 text-blue-800';
      case 'warn':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'debug':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleViewLogDetails = (log: LogEntry) => {
    setSelectedLog(log);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Logs</h1>
          <p className="text-gray-600 mt-1">
            Monitor all system activities, API requests, and application events
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`btn ${autoRefresh ? 'btn-primary' : 'btn-secondary'}`}
            title="Auto Refresh"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto On' : 'Auto Off'}
          </button>
          <button
            onClick={fetchLogs}
            className="btn btn-secondary"
            title="Refresh Now"
          >
            <ArrowPathIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search logs, endpoints, emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>

        <div className="flex items-center space-x-4">
          <FunnelIcon className="h-5 w-5 text-gray-400" />
          
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Levels</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="error">Error</option>
            <option value="debug">Debug</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Categories</option>
            <option value="api">API Requests</option>
            <option value="auth">Authentication</option>
            <option value="email">Email Service</option>
            <option value="database">Database</option>
            <option value="user_action">User Actions</option>
            <option value="system">System Events</option>
            <option value="security">Security Events</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="dashboard-card">
          <div className="space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 animate-pulse">
                <div className="w-4 h-4 bg-gray-200 rounded"></div>
                <div className="w-20 h-4 bg-gray-200 rounded"></div>
                <div className="flex-1 h-4 bg-gray-200 rounded"></div>
                <div className="w-16 h-4 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      ) : logs.length > 0 ? (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Message
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <motion.tr 
                    key={log.id} 
                    className="hover:bg-gray-50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <ClockIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getLevelIcon(log.level)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLevelBadgeClass(log.level)}`}>
                          {log.level.toUpperCase()}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(log.category)}
                        <span className="text-sm text-gray-900 capitalize">
                          {log.category.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-md truncate">
                        {log.message}
                      </div>
                      {log.details?.userEmail && (
                        <div className="text-xs text-gray-500 mt-1">
                          User: {log.details.userEmail}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <UserIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {log.createdBy}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {log.details?.statusCode && (
                          <div className={`text-xs px-2 py-1 rounded ${
                            log.details.statusCode < 300 ? 'bg-green-100 text-green-800' :
                            log.details.statusCode < 400 ? 'bg-blue-100 text-blue-800' :
                            log.details.statusCode < 500 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {log.details.statusCode}
                          </div>
                        )}
                        {log.details?.duration && (
                          <div className="text-xs text-gray-500">
                            {log.details.duration}
                          </div>
                        )}
                        {log.details?.ip && (
                          <div className="text-xs text-gray-500">
                            {log.details.ip}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleViewLogDetails(log)}
                        className="btn btn-ghost text-xs"
                        title="View Full Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="dashboard-card">
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Logs Found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || levelFilter !== 'all' || categoryFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'System logs will appear here as activities occur'
              }
            </p>
          </div>
        </div>
      )}

      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">Log Details</h3>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Timestamp</label>
                  <p className="text-sm text-gray-900">{formatTimestamp(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Level</label>
                  <div className="flex items-center space-x-2">
                    {getLevelIcon(selectedLog.level)}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getLevelBadgeClass(selectedLog.level)}`}>
                      {selectedLog.level.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Message</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">{selectedLog.message}</p>
              </div>

              {/* Details */}
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Request Details</label>
                  <div className="bg-gray-50 p-3 rounded text-sm space-y-1">
                    {Object.entries(selectedLog.details).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1')}:</span>
                        <span className="text-gray-900 font-mono">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Additional Data</label>
                  <pre className="bg-gray-50 p-3 rounded text-xs text-gray-900 overflow-auto">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setSelectedLog(null)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemLogsPage;